/**
 * OrgAdmin — Pilot Administration page.
 *
 * Scoped intentionally small for the pilot: aggregate counts + org
 * configuration (submission instructions, report preferences, branding,
 * invitations). No individual caregiver clinical data. No caseload
 * dashboard. No live monitoring.
 *
 * Sections:
 *   1) Pilot overview   — aggregate counts only
 *   2) Invite caregivers
 *   3) Sharing instructions   — how caregivers submit exported reports
 *   4) Organization branding
 *   5) Report preferences     — which report sections are surfaced
 *
 * Route stays at `/org-admin`.
 */

import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import OrgFamiliesWorkspace from "@/components/OrgFamiliesWorkspace";
import OrgMarketplaceSection from "@/components/OrgMarketplaceSection";
import { toast } from "sonner";
import {
    Building2, Copy, Check, Send, Mail, ListChecks, ClipboardList,
    Users, FileText, Loader2, Info,
} from "lucide-react";

/* ---------------- constants ---------------- */

const SUBMISSION_METHODS = [
    { key: "portal", label: "Patient portal (upload)" },
    { key: "secure_email", label: "Secure email" },
    { key: "fax", label: "Fax" },
    { key: "phone", label: "Phone call" },
    { key: "other", label: "Other / describe below" },
];

const REPORT_SECTIONS = [
    { key: "major_changes", label: "Major changes" },
    { key: "hospital_or_er", label: "Hospital or emergency visits" },
    { key: "medication_changes", label: "Medication changes" },
    { key: "behavior_safety", label: "Behavior and safety concerns" },
    { key: "eating_sleep", label: "Eating, drinking, and sleep changes" },
    { key: "care_team_service_changes", label: "Care-team and service changes" },
    { key: "upcoming_appointments", label: "Upcoming appointments" },
    { key: "questions_for_care_team", label: "Questions for the care team" },
    { key: "caregiver_concerns", label: "Caregiver concerns" },
    { key: "current_journey_area", label: "Current journey area" },
];

/* ---------------- page ---------------- */

export default function OrgAdmin() {
    const { user } = useAuth();
    const isPlatformAdmin = user?.role === "admin";

    const [orgs, setOrgs] = useState([]);
    const [activeOrgId, setActiveOrgId] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Load the caller's org(s). Platform admins pick from the whole list;
    // org admins see only theirs.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                if (isPlatformAdmin) {
                    const r = await api.get("/org/admin/list");
                    if (!active) return;
                    setOrgs(r.data || []);
                    const wanted = new URLSearchParams(window.location.search).get("org");
                    const match = (r.data || []).find(o => o.id === wanted);
                    if (r.data?.length) setActiveOrgId(match ? match.id : r.data[0].id);
                } else {
                    const r = await api.get("/org/me");
                    const adminOf = r.data?.is_org_admin_of || [];
                    const visible = (r.data?.orgs || []).filter(o => adminOf.includes(o.id));
                    if (!active) return;
                    setOrgs(visible);
                    if (visible.length) setActiveOrgId(visible[0].id);
                }
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [isPlatformAdmin]);

    // Load pilot stats for the active org.
    useEffect(() => {
        if (!activeOrgId) { setStats(null); return; }
        let active = true;
        (async () => {
            try {
                const r = await api.get(`/org/admin/${activeOrgId}/pilot-stats`);
                if (active) setStats(r.data);
            } catch (err) {
                if (active) toast.error("Couldn't load pilot stats.");
            }
        })();
        return () => { active = false; };
    }, [activeOrgId]);

    const reload = async () => {
        if (!activeOrgId) return;
        const r = await api.get(`/org/admin/${activeOrgId}/pilot-stats`);
        setStats(r.data);
    };

    const activeOrg = useMemo(() => orgs.find(o => o.id === activeOrgId) || null, [orgs, activeOrgId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-[#4C5551]">
                <Loader2 className="animate-spin mr-2" size={16} /> Loading pilot admin…
            </div>
        );
    }

    if (!orgs.length) {
        return (
            <div className="max-w-2xl mx-auto py-16 text-center" data-testid="org-admin-none">
                <Building2 size={40} className="mx-auto text-[#8B5A2B]" />
                <h1 className="mt-4 font-heading text-2xl text-[#12776F]">Pilot Administration</h1>
                <p className="mt-2 text-[#4C5551]">
                    You aren&apos;t connected to an organization pilot yet. If your team is piloting
                    Memory Lane, ask your Memory Lane contact to grant you access.
                </p>
            </div>
        );
    }

    const orgName = stats?.org?.name || activeOrg?.name || "Your organization";

    return (
        <div className="space-y-6 md:space-y-8" data-testid="org-admin-pilot-page">
            {/* Header */}
            <header className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-admin-header">
                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8B5A2B]">Pilot Administration</p>
                <h1 className="mt-1 font-heading font-semibold tracking-tight text-3xl md:text-4xl text-[#12776F]">
                    {orgName} <span className="text-teal-dark">Memory Lane Pilot</span>
                </h1>
                <p className="mt-2 text-[15px] text-[#4C5551]">
                    Caregiver communication and navigation support · Powered by Memory Lane
                </p>
                <a href="/org-intel" className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-[#1F2624] text-white hover:bg-teal-dark transition-colors" data-testid="org-admin-open-intel">
                    Open Intelligence Dashboard →
                </a>

                {isPlatformAdmin && orgs.length > 1 && (
                    <div className="mt-4">
                        <label className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Viewing</label>
                        <select
                            value={activeOrgId || ""}
                            onChange={e => setActiveOrgId(e.target.value)}
                            data-testid="org-admin-switch"
                            className="mt-1 block w-full max-w-md px-3 py-2 rounded-2xl border border-[#E2E8F0] bg-white text-sm"
                        >
                            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                    </div>
                )}
            </header>

            {!stats ? (
                <div className="flex items-center justify-center py-10 text-[#4C5551]">
                    <Loader2 className="animate-spin mr-2" size={14} /> Loading pilot stats…
                </div>
            ) : (
                <>
                    <OverviewSection stats={stats} onSaved={reload} />
                    <OrgFamiliesWorkspace orgId={activeOrgId} />
                    <OrgMarketplaceSection orgId={activeOrgId} />
                    <InviteCaregiversSection stats={stats} />
                    <SharingInstructionsSection stats={stats} onSaved={reload} />
                    <BrandingSection org={activeOrg} orgId={activeOrgId} onSaved={reload} />
                    <ReportPreferencesSection stats={stats} onSaved={reload} />
                </>
            )}

            <p className="text-xs italic text-[#6E7A74] pb-4" data-testid="org-admin-privacy-note">
                <Info size={12} className="inline mr-1" />
                This page shows aggregate pilot counts only. Individual caregiver clinical information
                is never visible here — caregivers own their data and choose what to share.
            </p>
        </div>
    );
}

/* ================================================================== */
/*  1. Pilot overview                                                  */
/* ================================================================== */

function OverviewSection({ stats, onSaved }) {
    const [busy, setBusy] = useState(false);
    const [meta, setMeta] = useState({
        pilot_start: stats.pilot_start || "",
        pilot_end: stats.pilot_end || "",
        pilot_contact_person: stats.pilot_contact_person || "",
    });

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/org/admin/${stats.org.id}/pilot-meta`, meta);
            toast.success("Pilot info saved.");
            onSaved?.();
        } catch {
            toast.error("Couldn't save pilot info.");
        } finally {
            setBusy(false);
        }
    };

    const counts = [
        { label: "Caregivers invited", value: stats.caregivers_invited, testid: "count-invited" },
        { label: "Caregivers activated", value: stats.caregivers_activated, testid: "count-activated" },
        { label: "Summaries generated", value: stats.summaries_generated, testid: "count-summaries" },
        { label: "Staff members", value: stats.staff_members, testid: "count-staff" },
    ];

    return (
        <section className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-admin-overview">
            <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-2xl bg-[#EFF4EC] flex items-center justify-center">
                    <ClipboardList size={17} className="text-teal-dark" />
                </span>
                <h2 className="font-heading text-xl md:text-2xl text-[#12776F]">Pilot overview</h2>
            </div>

            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                {counts.map(c => (
                    <div key={c.label} data-testid={c.testid} className="rounded-2xl border border-[#E9E2D4] bg-[#FDFAF3] p-4">
                        <p className="text-3xl font-heading font-semibold text-[#12776F]">{c.value}</p>
                        <p className="mt-1 text-xs text-[#4C5551]">{c.label}</p>
                    </div>
                ))}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <SmallField
                    label="Pilot start date" type="date" testid="pilot-start"
                    value={meta.pilot_start}
                    onChange={v => setMeta({ ...meta, pilot_start: v })}
                />
                <SmallField
                    label="Pilot end date" type="date" testid="pilot-end"
                    value={meta.pilot_end}
                    onChange={v => setMeta({ ...meta, pilot_end: v })}
                />
                <SmallField
                    label="Pilot contact person" testid="pilot-contact"
                    value={meta.pilot_contact_person}
                    onChange={v => setMeta({ ...meta, pilot_contact_person: v })}
                    placeholder="Name and role"
                />
            </div>
            <div className="mt-4 flex justify-end">
                <button
                    type="button" onClick={save} disabled={busy}
                    data-testid="pilot-meta-save"
                    className="inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white text-sm font-semibold px-5 py-2 disabled:opacity-60"
                >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save pilot info
                </button>
            </div>
        </section>
    );
}

/* ================================================================== */
/*  2. Invite caregivers                                               */
/* ================================================================== */

function InviteCaregiversSection({ stats }) {
    const code = stats.org?.code || "";
    const inviteUrl = `${window.location.origin}/login?code=${code}`;
    const [copied, setCopied] = useState(""); // "link" | "code" | "template"
    const [emailForm, setEmailForm] = useState({ email: "", family_name: "", intended_recipient: "" });
    const [sending, setSending] = useState(false);

    const emailTemplate = useMemo(() => (
        `Hi,\n\nWe're piloting Memory Lane, a caregiver navigation platform that helps you organize what's changing and share a clear summary with your care team.\n\nCreate your free account: ${inviteUrl}\nOr enter code ${code} on the sign-in page.\n\nEverything you enter stays yours. You'll be able to export a summary and submit it through our patient portal when you're ready.\n\nWith care,\n${stats.pilot_contact_person || "Your care team"}`
    ), [inviteUrl, code, stats.pilot_contact_person]);

    const copy = async (kind, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(kind);
            toast.success("Copied.");
            setTimeout(() => setCopied(""), 1500);
        } catch { toast.error("Copy failed."); }
    };

    const sendInvite = async (e) => {
        e.preventDefault();
        if (!emailForm.email) return;
        setSending(true);
        try {
            await api.post(`/org/admin/${stats.org.id}/invite-family`, {
                email: emailForm.email.trim().toLowerCase(),
                family_name: emailForm.family_name.trim(),
                intended_recipient: emailForm.intended_recipient.trim(),
            });
            toast.success(`Invite sent to ${emailForm.email}`);
            setEmailForm({ email: "", family_name: "", intended_recipient: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send that invite.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-admin-invites">
            <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-2xl bg-[#EBF2F8] flex items-center justify-center">
                    <Users size={17} className="text-teal-dark" />
                </span>
                <h2 className="font-heading text-xl md:text-2xl text-[#12776F]">Invite caregivers</h2>
            </div>
            <p className="mt-2 text-sm text-[#4C5551]">Send caregivers a branded link or share the code. Every caregiver&apos;s data stays private to them.</p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Invite link */}
                <div className="rounded-2xl border border-[#D5E6E4] bg-[#EDF7F5] p-4" data-testid="invite-link-card">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark">Caregiver invite link</p>
                    <p className="mt-1 font-mono text-sm text-[#1F2624] break-all">{inviteUrl}</p>
                    <button
                        type="button"
                        onClick={() => copy("link", inviteUrl)}
                        data-testid="copy-invite-link"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-dark hover:underline"
                    >
                        {copied === "link" ? <Check size={12} /> : <Copy size={12} />} Copy link
                    </button>
                </div>
                {/* Invite code */}
                <div className="rounded-2xl border border-[#F2DFC9] bg-[#FBEFE3] p-4" data-testid="invite-code-card">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[#8B3E1E]">Organization invite code</p>
                    <p className="mt-1 font-mono text-lg font-semibold text-[#1F2624] tracking-wider">{code}</p>
                    <button
                        type="button"
                        onClick={() => copy("code", code)}
                        data-testid="copy-invite-code"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#8B3E1E] hover:underline"
                    >
                        {copied === "code" ? <Check size={12} /> : <Copy size={12} />} Copy code
                    </button>
                </div>
            </div>

            {/* Email invitation form */}
            <div className="mt-5 rounded-2xl border border-[#E9E2D4] bg-[#FDFAF3] p-4">
                <p className="text-sm font-semibold text-[#1F2624]">Send by email</p>
                <p className="mt-1 text-xs text-[#4C5551]">Sends a Memory Lane welcome email with the invite link. Status shows as invited until they redeem.</p>
                <form onSubmit={sendInvite} className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <SmallField label="Caregiver email" type="email" required testid="invite-email"
                        value={emailForm.email} onChange={v => setEmailForm({ ...emailForm, email: v })} />
                    <SmallField label="Caregiver name (optional)" testid="invite-name"
                        value={emailForm.family_name} onChange={v => setEmailForm({ ...emailForm, family_name: v })} />
                    <SmallField label="Person receiving care (optional)" testid="invite-recipient"
                        value={emailForm.intended_recipient} onChange={v => setEmailForm({ ...emailForm, intended_recipient: v })} />
                    <div className="md:col-span-3 flex justify-end">
                        <button type="submit" disabled={sending}
                            data-testid="invite-send"
                            className="inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white text-sm font-semibold px-5 py-2 disabled:opacity-60">
                            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send invite
                        </button>
                    </div>
                </form>
            </div>

            {/* Email template */}
            <div className="mt-5">
                <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#1F2624]">Email invitation template</p>
                    <button
                        type="button"
                        onClick={() => copy("template", emailTemplate)}
                        data-testid="copy-template"
                        className="text-xs font-semibold text-teal-dark hover:underline inline-flex items-center gap-1"
                    >
                        {copied === "template" ? <Check size={12} /> : <Copy size={12} />} Copy template
                    </button>
                </div>
                <pre className="mt-2 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#1F2624] whitespace-pre-wrap font-sans">{emailTemplate}</pre>
            </div>

            <details className="mt-4 text-sm" data-testid="printable-instructions">
                <summary className="cursor-pointer text-teal-dark font-semibold hover:underline">Printable invitation instructions</summary>
                <div className="mt-2 rounded-2xl border border-[#E2E8F0] bg-white p-4 text-[#1F2624] leading-relaxed">
                    <p className="font-semibold">How to join {stats.org?.name || "our"} Memory Lane pilot:</p>
                    <ol className="mt-2 list-decimal ml-5 space-y-1">
                        <li>Visit <span className="font-mono">{inviteUrl}</span></li>
                        <li>Or go to the Memory Lane sign-in page and click <em>I have an invite code</em>.</li>
                        <li>Enter code <span className="font-mono font-semibold">{code}</span></li>
                        <li>Create your free Memory Lane account.</li>
                    </ol>
                    <p className="mt-3 text-xs text-[#4C5551]">Your information stays yours. You choose what to share back with our team.</p>
                </div>
            </details>
        </section>
    );
}

/* ================================================================== */
/*  3. Sharing instructions                                            */
/* ================================================================== */

function SharingInstructionsSection({ stats, onSaved }) {
    const [form, setForm] = useState({
        method: stats.submission_instructions?.method || "",
        portal_name: stats.submission_instructions?.portal_name || "",
        portal_url: stats.submission_instructions?.portal_url || "",
        secure_email: stats.submission_instructions?.secure_email || "",
        fax: stats.submission_instructions?.fax || "",
        phone: stats.submission_instructions?.phone || "",
        contact_person: stats.submission_instructions?.contact_person || "",
        caregiver_notice: stats.submission_instructions?.caregiver_notice || "",
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/org/admin/${stats.org.id}/submission-instructions`, form);
            toast.success("Sharing instructions saved.");
            onSaved?.();
        } catch {
            toast.error("Couldn't save.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-admin-sharing">
            <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-2xl bg-[#EBF2F8] flex items-center justify-center">
                    <FileText size={17} className="text-teal-dark" />
                </span>
                <h2 className="font-heading text-xl md:text-2xl text-[#12776F]">Sharing instructions</h2>
            </div>
            <p className="mt-2 text-sm text-[#4C5551]">
                Tell caregivers how to submit their Memory Lane summary through your existing systems.
                Memory Lane does not transmit reports automatically during the pilot — caregivers
                download or print the report and send it themselves.
            </p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Preferred submission method</label>
                    <select
                        value={form.method}
                        onChange={e => setForm({ ...form, method: e.target.value })}
                        data-testid="submission-method"
                        className="mt-1 block w-full px-3 py-2 rounded-2xl border border-[#E2E8F0] bg-white text-sm"
                    >
                        <option value="">Choose one…</option>
                        {SUBMISSION_METHODS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                </div>
                <SmallField label="Contact person" testid="submission-contact" value={form.contact_person} onChange={v => setForm({ ...form, contact_person: v })} />
                <SmallField label="Portal name" testid="submission-portal-name" value={form.portal_name} onChange={v => setForm({ ...form, portal_name: v })} />
                <SmallField label="Portal URL" testid="submission-portal-url" value={form.portal_url} onChange={v => setForm({ ...form, portal_url: v })} placeholder="https://…" />
                <SmallField label="Secure email address" type="email" testid="submission-email" value={form.secure_email} onChange={v => setForm({ ...form, secure_email: v })} />
                <SmallField label="Fax number" testid="submission-fax" value={form.fax} onChange={v => setForm({ ...form, fax: v })} />
                <SmallField label="Phone number" testid="submission-phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            </div>

            <div className="mt-4">
                <label className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Short instructions caregivers will see</label>
                <textarea
                    value={form.caregiver_notice}
                    onChange={e => setForm({ ...form, caregiver_notice: e.target.value })}
                    data-testid="submission-notice"
                    rows={3}
                    placeholder="When you are ready to share an update, download your Memory Lane summary and upload it through the ASV patient portal."
                    className="mt-1 block w-full px-3 py-2 rounded-2xl border border-[#E2E8F0] bg-white text-sm"
                />
            </div>

            <div className="mt-4 flex justify-end">
                <button type="button" onClick={save} disabled={busy}
                    data-testid="submission-save"
                    className="inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white text-sm font-semibold px-5 py-2 disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save instructions
                </button>
            </div>
        </section>
    );
}

/* ================================================================== */
/*  4. Organization branding                                           */
/* ================================================================== */

function BrandingSection({ org, orgId, onSaved }) {
    const [form, setForm] = useState({
        display_name: org?.brand?.display_name || org?.name || "",
        logo_url: org?.brand?.logo_url || "",
        color: org?.brand?.color || "#12776F",
        footer_text: org?.brand?.footer_text || "",
        support_url: org?.brand?.support_url || "",
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/org/admin/${orgId}/brand`, form);
            toast.success("Branding saved.");
            onSaved?.();
        } catch { toast.error("Couldn't save branding."); }
        finally { setBusy(false); }
    };

    return (
        <section className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-admin-branding">
            <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-2xl bg-[#EFF4EC] flex items-center justify-center">
                    <Building2 size={17} className="text-teal-dark" />
                </span>
                <h2 className="font-heading text-xl md:text-2xl text-[#12776F]">Organization branding</h2>
            </div>
            <p className="mt-2 text-sm text-[#4C5551]">
                Caregivers see a small &ldquo;Connected through {form.display_name || "your organization"}&rdquo; banner. Memory Lane branding stays as the primary experience.
            </p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <SmallField label="Organization name" testid="brand-name" value={form.display_name} onChange={v => setForm({ ...form, display_name: v })} />
                <SmallField label="Program name (optional)" testid="brand-program" value={form.footer_text} onChange={v => setForm({ ...form, footer_text: v })} placeholder="e.g. GUIDE Model Pilot" />
                <SmallField label="Logo URL" testid="brand-logo" value={form.logo_url} onChange={v => setForm({ ...form, logo_url: v })} placeholder="https://…" />
                <SmallField label="Support resources URL" testid="brand-support" value={form.support_url} onChange={v => setForm({ ...form, support_url: v })} placeholder="https://…" />
            </div>

            <div className="mt-4 flex justify-end">
                <button type="button" onClick={save} disabled={busy}
                    data-testid="brand-save"
                    className="inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white text-sm font-semibold px-5 py-2 disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save branding
                </button>
            </div>
        </section>
    );
}

/* ================================================================== */
/*  5. Report preferences                                              */
/* ================================================================== */

function ReportPreferencesSection({ stats, onSaved }) {
    const [prefs, setPrefs] = useState(() => {
        const p = stats.report_preferences || {};
        const out = {};
        REPORT_SECTIONS.forEach(s => { out[s.key] = p[s.key] !== false; });
        return out;
    });
    const [busy, setBusy] = useState(false);

    const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/org/admin/${stats.org.id}/report-preferences`, prefs);
            toast.success("Report preferences saved.");
            onSaved?.();
        } catch { toast.error("Couldn't save preferences."); }
        finally { setBusy(false); }
    };

    return (
        <section className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-admin-report-prefs">
            <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-2xl bg-[#FBEFE3] flex items-center justify-center">
                    <ListChecks size={17} className="text-[#8B3E1E]" />
                </span>
                <h2 className="font-heading text-xl md:text-2xl text-[#12776F]">Report preferences</h2>
            </div>
            <p className="mt-2 text-sm text-[#4C5551]">
                Choose which sections are recommended in caregivers&apos; Memory Lane summaries.
                Caregivers still choose what to include in their final report and review everything
                before exporting.
            </p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2">
                {REPORT_SECTIONS.map(s => (
                    <label
                        key={s.key}
                        data-testid={`pref-${s.key}`}
                        className={`flex items-center gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${prefs[s.key] ? "border-[#B8DBD8] bg-[#EDF7F5]" : "border-[#E2E8F0] bg-white hover:bg-[#F8FAFC]"}`}
                    >
                        <input
                            type="checkbox"
                            checked={!!prefs[s.key]}
                            onChange={() => toggle(s.key)}
                            className="accent-teal-dark w-4 h-4"
                        />
                        <span className="text-sm text-[#1F2624]">{s.label}</span>
                    </label>
                ))}
            </div>

            <div className="mt-4 flex justify-end">
                <button type="button" onClick={save} disabled={busy}
                    data-testid="pref-save"
                    className="inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white text-sm font-semibold px-5 py-2 disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save preferences
                </button>
            </div>
        </section>
    );
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

function SmallField({ label, testid, value, onChange, type = "text", placeholder = "", required = false }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">{label}</span>
            <input
                type={type}
                required={required}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="mt-1 block w-full px-3 py-2 rounded-2xl border border-[#E2E8F0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
            />
        </label>
    );
}
